import { useState } from "react";
import { ALL_SPECS, GROUPS } from "@/lib/sim/catalog";
import { ChevronDown, Search, X } from "lucide-react";

const SECTIONS: { id: string; name: string; groups: string[] }[] = [
  { id: "control", name: "دوائر السيطرة", groups: ["contactors", "aux", "relays", "buttons", "switches", "sensors"] },
  { id: "protection", name: "الحماية", groups: ["breakers", "rccb", "overload", "phase"] },
  { id: "dist", name: "التوزيع والتغذية", groups: ["terminals", "dist", "sources"] },
  { id: "loads", name: "المحركات والأحمال والقياس", groups: ["loads", "meters"] },
];


export function Palette({
  onAdd,
  onClose,
}: {
  onAdd: (type: string) => void;
  onClose?: () => void;
}) {
  const [open, setOpen] = useState<string[]>(["contactors", "buttons", "sources"]);
  const [q, setQ] = useState("");

  const toggle = (id: string) =>
    setOpen((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));

  const filtered = (g: string) =>
    ALL_SPECS.filter(
      (s) => s.group === g && (q === "" || s.name.includes(q) || s.type.includes(q.toLowerCase())),
    );

  return (
    <aside className="flex h-full w-full flex-col bg-sidebar sm:w-72">
      <div className="flex items-center gap-2 border-b border-border p-3">
        <div className="relative flex-1">
          <Search className="absolute end-2 top-2.5 size-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث عن عنصر..."
            className="w-full rounded-md border border-input bg-background py-2 pe-8 ps-3 text-sm outline-none focus:border-primary"
          />
        </div>
        {onClose && (
          <button
            onClick={onClose}
            aria-label="إغلاق المكتبة"
            className="rounded-md border border-border bg-card p-2 text-card-foreground hover:border-primary"
          >
            <X className="size-4" />
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {SECTIONS.map((sec) => {
          const groups = GROUPS.filter((g) => sec.groups.includes(g.id));
          if (!groups.some((g) => filtered(g.id).length)) return null;
          return (
            <div key={sec.id} className="mb-3">
              <div className="mb-1 px-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                {sec.name}
              </div>
              {groups.map((g) => {
                const items = filtered(g.id);
                if (!items.length) return null;
                const isOpen = open.includes(g.id) || q !== "";
                return (
                  <div key={g.id} className="mb-1.5">
                    <button
                      onClick={() => toggle(g.id)}
                      className="flex w-full items-center justify-between rounded-md bg-secondary px-3 py-2.5 text-sm font-semibold text-secondary-foreground transition hover:bg-accent"
                    >
                      <span>{g.name}</span>
                      <ChevronDown className={`size-4 transition ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && (
                      <div className="mt-1 space-y-1 ps-1">
                        {items.map((s) => (
                          <div
                            key={s.type}
                            data-device={s.type}
                            role="button"
                            tabIndex={0}
                            draggable
                            onDragStart={(e) => e.dataTransfer.setData("text/device", s.type)}
                            onClick={() => onAdd(s.type)}
                            onKeyDown={(e) => e.key === "Enter" && onAdd(s.type)}
                            title={s.info ?? s.name}
                            className="cursor-pointer rounded-md border border-border bg-card px-3 py-2.5 text-xs text-card-foreground transition hover:border-primary hover:bg-accent"
                          >
                            <div className="font-medium">{s.name}</div>
                            {s.info && (
                              <div className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
                                {s.info}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="border-t border-border p-3 text-[11px] leading-relaxed text-muted-foreground">
        انقر على العنصر لإضافته إلى مساحة العمل، أو اسحبه بالماوس.
      </div>
    </aside>
  );
}
